import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { cloneDeep } from "lodash"

import { DEFAULT_SELECTED_METRIC_KEY_LIST, REPORT_TYPE } from "@/app/report/common/constants"
import { buildGroupDisplayColumns, getColumnTitleKey } from "@/app/report/common/customTableColumns"
import { SettingSectionProps, StatsSettings } from "@/app/report/common/type"
import RadioGroup from "@/components/form/radioGroup/RadioGroup"
import TextField from "@/components/form/textField/TextField"
import Selector from "@/components/form/selector/Selector"
import Checkbox from "@/components/form/checkbox/Checkbox"
import MetricSelectorTable from "@/app/report/components/setting/MetricSelectorTable"
import ThresholdDialog from "@/app/report/components/setting/ThresholdDialog"
import { ReportTemplate } from "@/types/report"
import { MetricThreshold } from "@/types/threshold"
import { saveThresholds } from "@/utils/thresholdStorage"

const DEFAULT_CUSTOM_TABLE: StatsSettings = {
  title: "Table",
  fixedColumns: [],
  calcType: "average",
  metrics: DEFAULT_SELECTED_METRIC_KEY_LIST,
  summaryRows: { total: true, average: true },
}

// 고정 컬럼 구성원이 같은지(순서 무시). tools 의 isEqualUnordered 는 i18n 을 끌어와 테스트 환경에서 무거워 로컬 구현을 둔다.
const hasSameColumns = (a: string[], b: string[]) =>
  a.length === b.length && a.every((key) => b.includes(key))

type GroupOption = {
  value: string
  label: string
  info?: string
  columnKey?: string
}

const GROUP_OPTIONS: GroupOption[] = [
  { value: "session", label: "Session", info: "sessionStartDate", columnKey: "sessionName" },
  { value: "sessionType", label: "Session Type", info: "sessionCount", columnKey: "sessionType" },
  { value: "player", label: "Player", info: "uniformNumber", columnKey: "nickname" },
  { value: "position1", label: "Position (Depth 1)", info: "playerCount", columnKey: "position1" },
  { value: "position2", label: "Position (Depth 2)", info: "playerCount", columnKey: "position2" },
  { value: "playerGroup", label: "Player Group", info: "playerCount", columnKey: "playerGroup" },
]

const TIME_COLUMN_OPTIONS: Record<string, { key: string; label: string }[]> = {
  activity: [
    { key: "activityStartTime", label: "activityStartTime" },
    { key: "activityFinishTime", label: "activityFinishTime" },
  ],
  session: [
    { key: "sessionStartTime", label: "sessionStartTime" },
    { key: "sessionFinishTime", label: "sessionFinishTime" },
  ],
}

export default function CustomTableSettingsSection({
  settings,
  setTempTemplate,
  sectionIndex,
}: Omit<SettingSectionProps<StatsSettings>, "tempTemplate"> & { sectionIndex: number }) {
  const { t } = useTranslation()
  const [tempItem, setTempItem] = useState<StatsSettings>(DEFAULT_CUSTOM_TABLE)
  const [firstGroup, setFirstGroup] = useState<GroupOption | null>(null)
  const [secondGroup, setSecondGroup] = useState<GroupOption | null>(null)
  const [thresholdDialogOpen, setThresholdDialogOpen] = useState(false)
  const [thresholdMetricKey, setThresholdMetricKey] = useState("")
  const [thresholdMetricName, setThresholdMetricName] = useState("")
  // 마지막으로 동기화한 고정 컬럼 후보 목록. null 이면 아직 저장값을 하이드레이션한 직후다 (WEB-119)
  const syncedColumnKeysRef = useRef<string[] | null>(null)

  const secondGroupOptions = useMemo(() => {
    if (!firstGroup) {
      return []
    }

    const resetOption: GroupOption = { value: "", label: t("select"), info: "" }

    return [resetOption, ...GROUP_OPTIONS.filter((option) => option.value !== firstGroup.value)]
  }, [firstGroup])

  // 고정 컬럼 후보 = 리포트 표에 실제로 나오는 그룹 컬럼(선수 그룹이면 등번호·이름·포지션 …) — 표와 같은 빌더를 쓴다 (#99-8)
  const fixedColumnsForTable = useMemo(() => {
    if (!firstGroup) {
      return []
    }

    return buildGroupDisplayColumns(
      firstGroup.value,
      secondGroup?.value || null,
      REPORT_TYPE.DAILY,
    ).map((key) => ({ key, label: getColumnTitleKey(key) }))
  }, [firstGroup, secondGroup])

  const availableTimeColumns = useMemo(() => {
    const columns: { key: string; label: string }[] = []
    const groupValues = [firstGroup?.value, secondGroup?.value].filter(Boolean)

    groupValues.forEach((gv) => {
      if (gv && TIME_COLUMN_OPTIONS[gv]) {
        columns.push(...TIME_COLUMN_OPTIONS[gv])
      }
    })

    return columns
  }, [firstGroup, secondGroup])

  useEffect(() => {
    setTempItem({
      ...cloneDeep(settings),
    })
    // 저장값으로 다시 채워지므로 컬럼 비교 기준도 초기화한다 (WEB-119)
    syncedColumnKeysRef.current = null

    if (settings.groupKey1) {
      const group1 = GROUP_OPTIONS.find((opt) => opt.value === settings.groupKey1)
      setFirstGroup(group1 || null)
    }
    if (settings.groupKey2) {
      const group2 = GROUP_OPTIONS.find((opt) => opt.value === settings.groupKey2)
      setSecondGroup(group2 || null)
    }
  }, [settings])

  // 그룹 구성이 바뀔 때만 고정 컬럼 선택 상태를 재조정한다.
  // 진입(하이드레이션) 직후에는 저장된 고정 해제 상태를 그대로 존중해야 한다 (WEB-119)
  useEffect(() => {
    if (!firstGroup) {
      return
    }

    const columnKeys = fixedColumnsForTable.map((col) => col.key)
    const previousColumnKeys = syncedColumnKeysRef.current
    syncedColumnKeysRef.current = columnKeys

    setTempItem((prev) => {
      // 이미 있던 컬럼은 선택 상태 유지, 그룹 교체로 새로 생긴 컬럼만 기본 고정
      const nextFixedColumns = columnKeys.filter((key) =>
        previousColumnKeys === null || previousColumnKeys.includes(key)
          ? (prev.fixedColumns || []).includes(key)
          : true,
      )

      return {
        ...prev,
        // 구성원이 같으면 저장된 순서를 그대로 둔다 — 컬럼 순서 변경(#99)으로 저장값과 순서만 달라져
        // 진입 직후 SAVE 가 활성되는 일을 막는다 (WEB-119 재비활성 규칙 유지)
        fixedColumns: hasSameColumns(nextFixedColumns, prev.fixedColumns || [])
          ? prev.fixedColumns || []
          : nextFixedColumns,
        groupKey1: firstGroup.value,
        groupKey2: previousColumnKeys === null ? prev.groupKey2 : secondGroup?.value || null,
      }
    })
  }, [fixedColumnsForTable, firstGroup, secondGroup])

  useEffect(() => {
    setTempTemplate((prev: ReportTemplate | null) => {
      if (!prev) {
        return prev
      }

      const updatedContent = prev.content.map((section, index) => {
        if (index === sectionIndex) {
          return { ...section, settings: tempItem }
        }

        return section
      })

      return { ...prev, content: updatedContent }
    })
  }, [sectionIndex, tempItem, setTempTemplate])

  return (
    <div className="TrendSettingsActivityStatsSectionCont">
      <div className="trend-setting-activity-xAxis-row">
        <TextField
          className="trend-setting-activity-xAxis"
          label={t("name")}
          value={tempItem.title}
          onChange={(e) => {
            setTempItem((prev) => {
              const updated = { ...prev }
              updated.title = e.target.value

              return updated
            })
          }}
          error={tempItem.title.length > 35 || tempItem.title.length < 1}
          showMessage={tempItem.title.length > 35 || tempItem.title.length < 1}
          message={
            tempItem.title.length > 35
              ? t("rules.max_len", { name: t("title"), max_len: 35 })
              : t("rules.required")
          }
        />
        <div className="trend-setting-activity-stats-radio">
          <p className="label-text">{t("metricCalculationOption")}</p>
          <RadioGroup
            direction="row"
            items={[
              { label: t("average"), value: "average" },
              { label: t("total"), value: "total" },
            ]}
            value={tempItem?.calcType || "average"}
            onChange={(_, newVal) => {
              if (!newVal) {
                return
              }
              setTempItem((prev) => {
                const updated = { ...prev }
                updated.calcType = newVal

                return updated
              })
            }}
          />
        </div>
      </div>
      <div className="trend-setting-custom-table-group-row">
        <Selector
          label={t("1stGroup")}
          options={GROUP_OPTIONS}
          value={firstGroup}
          onChange={(_: unknown, newVal: GroupOption | null) => {
            setFirstGroup(newVal)
            setSecondGroup(null)
          }}
          placeholder={t("select")}
          className="trend-setting-custom-table-group-selector"
        />
        <Selector
          label={t("2ndGroup")}
          options={secondGroupOptions}
          value={secondGroup}
          onChange={(_: unknown, newVal: GroupOption | null) => {
            setSecondGroup(newVal?.value ? newVal : null)
          }}
          placeholder={t("select")}
          disabled={!firstGroup}
          className="trend-setting-custom-table-group-selector"
        />
      </div>
      {/* Summary Row(#155): 화면·PDF·CSV 상단 요약 행 표시 여부. 필드가 없는 기존 표는 둘 다 해제 상태 */}
      <div className="trend-setting-activity-fixed-columns trend-setting-summary-rows">
        <p className="label-text">{t("summaryRow", "Summary Row")}</p>
        <div>
          {(["average", "total"] as const).map((kind) => (
            <Checkbox
              key={kind}
              checked={tempItem.summaryRows?.[kind] === true}
              label={kind === "average" ? t("avg", "Avg") : t("total", "Total")}
              value={kind}
              onChange={(_, checked) => {
                setTempItem((prev) => ({
                  ...prev,
                  summaryRows: {
                    total: prev.summaryRows?.total === true,
                    average: prev.summaryRows?.average === true,
                    [kind]: checked,
                  },
                }))
              }}
            />
          ))}
        </div>
      </div>
      {fixedColumnsForTable.length > 0 && (
        <div className="trend-setting-activity-fixed-columns">
          <p className="label-text">{t("fixedColumn")}</p>
          <div>
            {fixedColumnsForTable.map((column) => (
              <Checkbox
                checked={tempItem.fixedColumns.includes(column.key)}
                key={column.key}
                label={t(column.label)}
                value={column.key}
                onChange={(_, checked) => {
                  setTempItem((prev) => {
                    const selected = new Set(prev.fixedColumns || [])
                    if (checked) {
                      selected.add(column.key)
                    } else {
                      selected.delete(column.key)
                    }

                    return {
                      ...prev,
                      // 컬럼 순서로 정규화 — 재체크 후 저장값과 동일해져야 SAVE 가 다시 비활성된다 (WEB-119)
                      fixedColumns: fixedColumnsForTable
                        .map((col) => col.key)
                        .filter((key) => selected.has(key)),
                    }
                  })
                }}
              />
            ))}
          </div>
        </div>
      )}
      <MetricSelectorTable
        settings={settings}
        setTempSelectedMetricKeyList={(value) => {
          setTempItem((prev) => {
            const updated = { ...prev }
            updated.metrics = value

            return updated
          })
        }}
        fixedColumns={[]}
        additionalItems={availableTimeColumns}
        thresholds={tempItem.thresholds}
        onThresholdClick={(metricKey, metricName) => {
          setThresholdMetricKey(metricKey)
          setThresholdMetricName(metricName)
          setThresholdDialogOpen(true)
        }}
      />
      <ThresholdDialog
        open={thresholdDialogOpen}
        onClose={() => setThresholdDialogOpen(false)}
        metricKey={thresholdMetricKey}
        metricName={thresholdMetricName}
        threshold={tempItem.thresholds?.find((th) => th.metricKey === thresholdMetricKey)}
        onSave={(threshold: MetricThreshold) => {
          setTempItem((prev) => {
            const updated = { ...prev }
            const existing = updated.thresholds || []
            const filtered = existing.filter((th) => th.metricKey !== threshold.metricKey)

            if (threshold.rules.length > 0) {
              updated.thresholds = [...filtered, threshold]
            } else {
              updated.thresholds = filtered.length > 0 ? filtered : undefined
            }

            // 프로토타입: localStorage에 저장
            saveThresholds(updated.title, updated.thresholds || [])

            return updated
          })
        }}
      />
    </div>
  )
}
